// src/api/auth.ts
import apiClient, { createFormData, handleApiError } from './client';
import type { User } from '@/types/user';

export async function login(email: string, password: string) {
  try {
    const formData = createFormData({ username: email, password });
    const { data: authResponse } = await apiClient.post('/api/auth/login', formData, {
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded'
      }
    });

    return {
      token: authResponse.access_token
    };
  } catch (error) {
    console.error('Login failed:', handleApiError(error));
    return false;
  }
}

export async function oauthLogin(provider: string, token: string) {
  try {
    const { data } = await apiClient.post(`/api/auth/${provider}`, { token });
    // Note: JWT is stored in httpOnly cookie by the backend
    return true;
  } catch (error) {
    console.error('OAuth login failed:', handleApiError(error));
    return false;
  }
}

export async function register(email: string, password: string, firstName: string, lastName: string, phone: string) {
  try {
    const formData = createFormData({ 
      email,
      password,
      first_name: firstName,
      last_name: lastName,
      phone
    });
    
    await apiClient.post('/api/auth/register', formData, {
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded'
      }
    });

    return true;
  } catch (error) {
    console.error('Registration failed:', handleApiError(error));
    return false;
  }
}

export async function forgotPassword(email: string) {
  try {
    const formData = createFormData({ email });
    await apiClient.post('/api/auth/forgot-password', formData, {
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded'
      }
    });
    return true;
  } catch (error) {
    console.error('Forgot password failed:', handleApiError(error));
    throw error;
  }
}

export async function checkToken() {
  try {
    const response = await apiClient.get<User>('/api/auth/current-user');
    return { isValid: true, user: response.data };
  } catch {
    return { isValid: false, user: null };
  }
}

export async function logout() {
  try {
    await apiClient.post('/api/auth/logout');
    return true;
  } catch (error) {
    console.error('Failed to call logout endpoint:', handleApiError(error));
    return false;
  }
}

export async function refreshSession() {
  try {
    const response = await apiClient.post('/api/auth/refresh-session');
    return response.data;
  } catch (error) {
    console.error('Failed to refresh session:', handleApiError(error));
    throw error;
  }
}

export async function getWebSocketToken() {
  try {
    const response = await apiClient.get('/api/auth/websocket-token');
    return response.data.token;
  } catch (error) {
    console.error('Failed to get WebSocket token:', handleApiError(error));
    throw error;
  }
}